import React, { useState, useEffect, useMemo } from "react";
import { Form, Button, Table, InputGroup, Badge } from "react-bootstrap";
import { useFormikContext } from "formik";
import { FaPlusCircle, FaTrashAlt, FaShoppingBag, FaHistory, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { getAllDuePurchasesForSupplierId } from "../../redux/features/purchase.features";
import { fetchDueSalesForCustomerId } from "../../redux/features/sales.features";
import { formatCurrency } from "../../utility";
import { ShoppingBag, History, CheckCircle, AlertCircle } from "lucide-react";

const PurchasePaymentField = ({ agent_id }) => {
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext();
  const [selectedPurchases, setSelectedPurchases] = useState(values.references?.purchase_orders || []);
  const { supplierDuePurchases } = useSelector((state) => state.purchaseReducer);
  const { customerDueSales } = useSelector((state) => state.salesReducer);

  const isCustomerReceipt = values.transaction_type === "Customer Receipt";

  // Fetch dues when recipient or type changes
  useEffect(() => {
    if (values.references?.supplier) {
      if (isCustomerReceipt) {
        dispatch(fetchDueSalesForCustomerId(values.references.supplier));
      } else {
        dispatch(getAllDuePurchasesForSupplierId(values.references.supplier));
      }
    }
  }, [dispatch, values.references?.supplier, values.transaction_type]);

  // Unified Dues List
  const rawDues = isCustomerReceipt ? customerDueSales : supplierDuePurchases;

  // FILTER LOGIC: Filter by Agent if provided (only for purchases)
  const filteredPurchased = useMemo(() => {
    if (!rawDues) return [];
    if (isCustomerReceipt || !agent_id) return rawDues;
    return rawDues.filter(p => (p.ref_agent_id || "direct") === agent_id);
  }, [rawDues, agent_id, isCustomerReceipt]);

  // Sync with Formik
  useEffect(() => {
    setFieldValue("references.purchase_orders", selectedPurchases);

    // Recalculate total payable
    const totalAmount = selectedPurchases.reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
    setFieldValue("payAbleAmount", totalAmount);

    // Default sync amount and first payment method
    setFieldValue("amount", totalAmount);
    if (values.payment_methods?.[0]) {
      setFieldValue("payment_methods[0].amount", totalAmount);
    }
  }, [selectedPurchases, setFieldValue]);

  const handleAddPurchaseOrder = (purchase) => {
    if (selectedPurchases.some(p => p.purchase_id === purchase._id)) return;

    const updated = [...selectedPurchases, {
      purchase_id: purchase._id,
      referenceNumber: purchase.referenceNumber, // Tracking for UI
      grand_total: purchase.grand_total,
      payment_due_amount: purchase.payment_due_amount,
      paidAmount: purchase.payment_due_amount
    }];
    setSelectedPurchases(updated);
  };

  const handleRemovePurchaseOrder = (id) => {
    setSelectedPurchases(prev => prev.filter(p => p.purchase_id !== id));
  };

  const handleAmountPaidChange = (index, val) => {
    const updated = [...selectedPurchases];
    updated[index].paidAmount = parseFloat(val) || 0;
    setSelectedPurchases(updated);
  };

  return (
    <div className="erp-settlement-widget">
      <style>
        {`
            .erp-table-premium {
                border: 1px solid #f1f5f9;
                border-radius: 16px;
                overflow: hidden;
                background: white;
            }
            .erp-table-premium thead {
                background: #f8fafc;
            }
            .erp-table-premium th {
                font-size: 11px;
                font-weight: 800;
                color: #64748b;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                padding: 16px;
                border-bottom: 2px solid #f1f5f9;
            }
            .erp-table-premium td {
                padding: 14px 16px;
                font-size: 13px;
                vertical-align: middle;
                border-bottom: 1px solid #f1f5f9;
            }
            .purchase-row-select {
                cursor: pointer;
                transition: background 0.2s;
            }
            .purchase-row-select:hover {
                background: #f0f9ff;
            }
            .add-btn-refined {
                border-radius: 8px;
                font-size: 11px;
                font-weight: 800;
                padding: 6px 12px;
                text-transform: uppercase;
            }
            .selection-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;
            }
        `}
      </style>

      {/* SEARCH RESULTS / AVAILABLE DUES */}
      <div className="selection-header">
        <div className="d-flex align-items-center gap-2">
          <History size={18} className="text-secondary" />
          <h6 className="mb-0 fw-bold text-slate-700">Outstanding Invoices (Dues)</h6>
          <Badge bg="soft-primary" className="ms-2 px-2 py-1 text-primary" style={{ background: '#eff6ff', borderRadius: '6px' }}>
            {filteredPurchased.length} Available
          </Badge>
        </div>
      </div>

      <div className="erp-table-premium mb-5">
        <Table responsive hover className="mb-0 border-0">
          <thead>
            <tr>
              <th>Invoice / Ref No</th>
              <th>Due Amount</th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPurchased.length > 0 ? (
              filteredPurchased.map((purchase) => {
                const isSelected = selectedPurchases.some(p => p.purchase_id === purchase._id);
                return (
                  <tr key={purchase._id} className="purchase-row-select">
                    <td className="fw-bold text-dark">
                      {purchase.referenceNumber}
                      <div className="x-small text-muted fw-normal">Total: {formatCurrency(purchase.grand_total)}</div>
                    </td>
                    <td className="fw-bold text-danger">
                      {formatCurrency(purchase.payment_due_amount)}
                    </td>
                    <td className="text-center">
                      <Button
                        variant={isSelected ? "light" : "outline-primary"}
                        size="sm"
                        className="add-btn-refined"
                        disabled={isSelected}
                        onClick={() => handleAddPurchaseOrder(purchase)}
                      >
                        {isSelected ? <CheckCircle size={14} className="me-1" /> : <FaPlusCircle className="me-1" />}
                        {isSelected ? "Added" : "Add"}
                      </Button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className="text-center py-5">
                  <div className="opacity-25 mb-2"><ShoppingBag size={40} /></div>
                  <div className="small fw-bold text-muted uppercase">No pending dues found for this context</div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* SELECTED SETTLEMENTS */}
      {selectedPurchases.length > 0 && (
        <div className="animate__animated animate__fadeIn">
          <div className="selection-header">
            <div className="d-flex align-items-center gap-2">
              <CheckCircle size={18} className="text-success" />
              <h6 className="mb-0 fw-bold text-slate-700">Settlement Allocation</h6>
            </div>
            <Button variant="link" size="sm" className="text-danger fw-bold text-decoration-none x-small" onClick={() => setSelectedPurchases([])}>
              CLEAR ALL
            </Button>
          </div>

          <div className="erp-table-premium shadow-sm">
            <Table responsive className="mb-0 border-0">
              <thead>
                <tr style={{ background: '#f0fdf4' }}>
                  <th>Reference</th>
                  <th>Current Due</th>
                  <th style={{ width: '180px' }}>Allocation (Rs.)</th>
                  <th className="text-center"></th>
                </tr>
              </thead>
              <tbody>
                {selectedPurchases.map((p, idx) => (
                  <tr key={p.purchase_id}>
                    <td className="fw-bold text-dark">{p.referenceNumber}</td>
                    <td className="text-muted fw-bold">{formatCurrency(p.payment_due_amount)}</td>
                    <td className="py-2">
                      <InputGroup size="sm" className="erp-input-group p-0 border-0 shadow-none">
                        <Form.Control
                          type="number"
                          value={p.paidAmount}
                          onChange={(e) => handleAmountPaidChange(idx, e.target.value)}
                          className="fw-bold text-success border-0 bg-light text-center"
                        />
                      </InputGroup>
                    </td>
                    <td className="text-center">
                      <Button variant="link" className="text-danger p-0" onClick={() => handleRemovePurchaseOrder(p.purchase_id)}>
                        <FaTrashAlt size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchasePaymentField;
